import { Dialog, Icon, usePermissions } from "@sarva/ui";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NAV_BUILT_LEAVES } from "@/app/nav";

/**
 * The ⌘K palette behind the sidebar's search box.
 *
 * Deliberately navigation-only for now. A search box that promises to find a
 * claim and returns nothing is worse than one that is honestly scoped, and
 * there is no API to search yet (doc 27 §5: no raw fetch, no invented
 * endpoint). The placeholder says what it searches.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const { permissions } = usePermissions();

  // (H) doc 27 §7: the palette never offers a destination the user cannot
  // open. It is ergonomics, not security — the API remains the authority.
  const reachable = useMemo(
    () =>
      // An ungated leaf is offered to everybody: the landing route needs no permission,
      // and asking the set whether it holds `undefined` would hide it from every session.
      NAV_BUILT_LEAVES.filter(
        (leaf) => leaf.permission === undefined || permissions.has(leaf.permission),
      ),
    [permissions],
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return reachable;
    return reachable.filter((leaf) => leaf.label.toLowerCase().includes(q));
  }, [reachable, query]);

  const go = (index: number): void => {
    const target = results[index];
    if (!target) return;
    onOpenChange(false);
    setQuery("");
    setActive(0);
    navigate(target.to);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Go to"
      description="Jump to a screen. Type to filter, ↑ ↓ to move, Enter to open."
      size="md"
    >
      <div className="flex flex-col gap-2">
        <div className="relative">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2">
            <Icon name="search" size={4} className="h-3.5 w-3.5 text-tertiary" />
          </span>
          {/* A palette that does not focus its own input is a palette nobody
              uses twice. Radix's dialog focus-trap hands focus here on open. */}
          <input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((i) => (i + 1) % Math.max(results.length, 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((i) => (i - 1 + results.length) % Math.max(results.length, 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                go(active);
              }
            }}
            placeholder="Search screens…"
            aria-label="Search screens"
            className={[
              "h-8 w-full rounded-sm border border-control bg-default pl-8 pr-2.5",
              "text-sm text-default placeholder:text-placeholder",
              "outline-none focus-visible:border-strong focus-visible:shadow-focus-ring",
            ].join(" ")}
          />
        </div>
        <ul className="max-h-dropdown overflow-y-auto">
          {results.length === 0 ? (
            <li className="px-2 py-6 text-center text-sm text-tertiary">
              Nothing matches “{query}”. This searches screen names only.
            </li>
          ) : (
            results.map((leaf, i) => (
              <li key={leaf.to}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(i)}
                  className={[
                    "flex h-9 w-full items-center gap-2 rounded-sm px-2 text-sm",
                    i === active ? "bg-hover text-default" : "text-secondary",
                  ].join(" ")}
                >
                  <Icon name={leaf.icon} size={4} className="text-tertiary" />
                  <span className="flex-1 text-left">{leaf.label}</span>
                  {i === active ? (
                    <Icon name="arrow-right" size={4} className="text-tertiary" />
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </Dialog>
  );
}
